import { Download, Pause, Play } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.ts';
import { Card } from '../card.tsx';

export interface AudioPlayerProps {
  /** Audio source URL fed to the hidden <audio> element. */
  src: string;
  /** Optional waveform amplitudes (0..1). When absent a deterministic
   *  pseudo-waveform is derived so the strip still reads as audio. */
  peaks?: number[];
  /** Optional total-duration label; otherwise derived from the audio element. */
  durationLabel?: string;
  /** Download target; falls back to `src`. */
  downloadHref?: string;
  /** Header label; defaults to "Recording". */
  title?: string;
  /** Small mono uppercase note (e.g. "Internal diagnostic · sample"). */
  sampleNote?: string;
  'data-testid'?: string;
}

const BAR_COUNT = 56;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Deterministic pseudo-waveform from a sine over the bar index, so a player
 *  with no `peaks` still looks like audio (no randomness, reduced-motion safe). */
function derivePeaks(count: number): number[] {
  return Array.from({ length: count }, (_, i) => {
    const t = i / count;
    return 0.25 + 0.7 * Math.abs(Math.sin(t * Math.PI * 6) * Math.cos(t * Math.PI * 2));
  });
}

/**
 * AudioPlayer — a warm, presentational player around a hidden <audio>. Real
 * playback, a clickable/keyboard-seekable waveform strip, a mono time readout
 * and a download anchor. Purely controlled by the audio element's own events;
 * no provider SDK, no network beyond the given `src`.
 */
export function AudioPlayer({
  src,
  peaks,
  durationLabel,
  downloadHref,
  title,
  sampleNote,
  'data-testid': testId = 'audio-player',
}: AudioPlayerProps) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);

  const bars = React.useMemo(() => {
    const raw = peaks && peaks.length > 0 ? peaks : derivePeaks(BAR_COUNT);
    return raw.map((peak, i) => ({
      key: `bar-${i}`,
      // Centre position of the bar (0..1), used to colour the played portion.
      mid: (i + 0.5) / raw.length,
      height: Math.max(0.12, Math.min(1, peak)),
    }));
  }, [peaks]);

  const ratio = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;

  const togglePlay = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Drive the toggle off our own playing state (mirrored from the audio
    // element's play/pause events) rather than `audio.paused`, which keeps the
    // control responsive even where `paused` lags the user gesture.
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
  }, [isPlaying]);

  const seekToRatio = React.useCallback(
    (r: number) => {
      const audio = audioRef.current;
      const clamped = Math.min(1, Math.max(0, r));
      const target = clamped * (duration || 0);
      if (audio && Number.isFinite(target)) {
        audio.currentTime = target;
      }
      setCurrentTime(target);
    },
    [duration],
  );

  const handleSeekPointer = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.width === 0) return;
      seekToRatio((event.clientX - rect.left) / rect.width);
    },
    [seekToRatio],
  );

  const handleSeekKey = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = 0.05;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        seekToRatio(ratio + step);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        seekToRatio(ratio - step);
      } else if (event.key === 'Home') {
        event.preventDefault();
        seekToRatio(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        seekToRatio(1);
      }
    },
    [ratio, seekToRatio],
  );

  const handleRootKey = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        togglePlay();
      }
    },
    [togglePlay],
  );

  const totalLabel = durationLabel ?? formatTime(duration);

  return (
    <Card
      data-testid={testId}
      tabIndex={0}
      onKeyDown={handleRootKey}
      className={cn(
        'flex flex-col gap-3 p-4',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
      )}
    >
      {/* biome-ignore lint/a11y/useMediaCaption: operator-fed recording; captions
          are not available for raw call audio. */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setDuration(d);
        }}
      />

      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <span className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]">
          {title ?? 'Recording'}
        </span>
        {sampleNote ? (
          <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)]">
            {sampleNote}
          </span>
        ) : null}
      </div>

      {/* Controls + waveform + time + download */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="audio-player-playpause"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          aria-pressed={isPlaying}
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            'bg-[var(--color-charcoal)] text-[var(--color-alabaster)]',
            'transition-colors hover:bg-[var(--color-stone-700)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
            'cursor-pointer',
          )}
        >
          {isPlaying ? (
            <Pause className="h-4 w-4 fill-current" />
          ) : (
            <Play className="h-4 w-4 fill-current" />
          )}
        </button>

        {/* Waveform / seek surface */}
        <div
          data-testid="audio-player-seek"
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuetext={`${formatTime(currentTime)} of ${totalLabel}`}
          onClick={handleSeekPointer}
          onKeyDown={handleSeekKey}
          className={cn(
            'flex h-10 min-w-0 flex-1 cursor-pointer items-end gap-[2px] overflow-hidden rounded-[var(--radius-xs)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
          )}
        >
          {bars.map((bar) => (
            <span
              key={bar.key}
              aria-hidden="true"
              className="min-w-0 flex-1 rounded-full"
              style={{
                height: `${bar.height * 100}%`,
                backgroundColor:
                  bar.mid <= ratio ? 'var(--color-warm-stone-deep)' : 'var(--color-warm-stone)',
              }}
            />
          ))}
        </div>

        {/* Time readout */}
        <span
          data-testid="audio-player-time"
          className="shrink-0 font-[var(--font-mono)] text-xs tabular-nums text-[var(--color-stone-mid)]"
        >
          {formatTime(currentTime)} / {totalLabel}
        </span>

        {/* Download */}
        <a
          data-testid="audio-player-download"
          href={downloadHref ?? src}
          download
          aria-label="Download recording"
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
            'border border-[var(--color-warm-stone-soft)] text-[var(--color-charcoal)]',
            'transition-colors hover:bg-[var(--color-canvas)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
          )}
        >
          <Download className="h-4 w-4" />
        </a>
      </div>
    </Card>
  );
}
